import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { MailIcon } from "lucide-react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";
import useViewerRole from "@/lib/rights/use-viewer-role";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";
import ResendEmailDialog from "./resend-email-dialog";
import {
  getResendRecipient,
  getResentFromLogId,
  isResendableEmail,
  SUPERADMIN_RESEND_LOG_AUTHOR,
} from "./resend-email";

type EmailActivityProps = {
  activity: ReturnRequestLogDto;
  returnRequestId: string;
  /** Date of the latest resend of this entry, if any. */
  lastResendAt?: number;
};

export default function EmailActivity({
  activity,
  returnRequestId,
  lastResendAt,
}: EmailActivityProps) {
  const t = useTranslation();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const viewerRole = useViewerRole();
  const { data: shop } = useCurrentShopSuspense();

  const emailNameByType = {
    newReturnRequest: t("returnDetail.requestCreation"),
    updateRequestStatus: t("returnDetail.requestUpdate"),
    newReturnRequestWithoutValidation: t(
      "returnDetail.newReturnRequestWithoutValidation",
    ),
    returnRequestValidated: t("returnDetail.returnRequestValidated"),
    discountCode: t("returnDetail.discountCode"),
    newReturnRequestWithoutLabel: t(
      "returnDetail.newReturnRequestWithoutLabel",
    ),
    newReturnRequestWithoutPhysicalReturn: t(
      "returnDetail.newReturnRequestWithoutPhysicalReturn",
    ),
    newReturnRequestReturnToStore: t(
      "returnDetail.newReturnRequestReturnToStore",
    ),
    newLabel: t("returnDetail.newLabel"),
    depositReminder: t("returnDetail.depositReminder"),
    returnRequestRejected: t("returnDetail.returnRequestRejected"),
    storeCreditCreated: t("returnDetail.storeCreditCreated"),
  };

  // Workflow-sent emails carry the template identity in params — the label
  // is the template title (legacy types map to fixed labels below instead).
  const workflowTemplateTitle =
    activity.emailSent === "workflow"
      ? (activity.params as { templateTitle?: string } | undefined)
          ?.templateTitle || t("returnDetail.workflowEmail")
      : undefined;

  const canResend =
    (isSuperAdmin || viewerRole === "admin" || viewerRole === "owner") &&
    isResendableEmail(activity);

  const isHiddenSuperAdminResend =
    activity.author === SUPERADMIN_RESEND_LOG_AUTHOR && !isSuperAdmin;

  // Shop-member resends carry no comment: the line is rendered from params.
  const resendRecipient = getResentFromLogId(activity)
    ? getResendRecipient(activity)
    : undefined;
  // `author` is the member's user id, like every other member-authored entry.
  const resendAuthorName = resendRecipient
    ? getAuthorName(
        shop.users.find((user) => user.userId === activity.author)?.user,
        activity.author ?? "",
      )
    : null;

  if (isHiddenSuperAdminResend) {
    return null;
  }

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <MailIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2">
          <ActivityItemHeader
            title={t("activities.email.title")}
            timestamp={activity.date}
          >
            <span className="font-medium text-xs underline">
              {workflowTemplateTitle ||
                emailNameByType[
                  activity.emailSent as keyof typeof emailNameByType
                ] ||
                ""}
            </span>
          </ActivityItemHeader>
        </div>
        {activity.comment && (
          <p className="text-xs text-amber-700">{activity.comment}</p>
        )}
        {!activity.comment && resendRecipient && (
          <p className="text-xs text-slate-500">
            {t("activities.email.resentTo", {
              recipient: resendRecipient,
              author: resendAuthorName ?? "",
            })}
          </p>
        )}
        {canResend && (
          <ResendEmailDialog
            activity={activity}
            returnRequestId={returnRequestId}
            isSuperAdmin={isSuperAdmin}
            lastResendAt={lastResendAt}
          />
        )}
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
